import React, { ChangeEvent, useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { EvidenciaImg } from "../types/ot.types";

interface FileUploaderPreviewProps {
  label: string;
  archivos: EvidenciaImg[];
  setArchivos: React.Dispatch<React.SetStateAction<EvidenciaImg[]>>;
  accept?: string;
  multiple?: boolean;
  disabled?: boolean;
}

export const FileUploaderPreview: React.FC<FileUploaderPreviewProps> = ({
  label,
  archivos,
  setArchivos,
  accept = "image/*, video/mp4, application/pdf",
  multiple = true,
  disabled = false,
}) => {
  const [previewFile, setPreviewFile] = useState<EvidenciaImg | null>(null);
  const [zoom, setZoom] = useState<number>(100);

  useEffect(() => {
    setZoom(100);
    if (previewFile) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [previewFile]);

  const handleUpload = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files).map(
        (file) =>
          ({
            id: Math.random().toString(36).substring(7) + Date.now(),
            url: URL.createObjectURL(file),
            file: file,
            tipo: file.type.includes("pdf") ? "pdf" : file.type.includes("video") ? "video" : "imagen",
            nombreOriginal: file.name,
          }) as EvidenciaImg,
      );
      setArchivos((prev) => (multiple ? [...prev, ...newFiles] : newFiles));
    }
    e.target.value = "";
  };

  const removeFile = (id: string) => {
    setArchivos((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div className="form-group mb-3">
      <label className="fw-bold mb-1" >
        {label} <i className="fas fa-paperclip text-secondary ms-1"></i>
      </label>
      {!disabled && (
        <input
          type="file"
          className="form-control shadow-sm"
          multiple={multiple}
          accept={accept}
          onChange={handleUpload}
        />
      )}
      {disabled && archivos.length === 0 && (
        <div className="alert alert-secondary py-2 mt-2" role="alert">
          <i className="fas fa-info-circle me-2"></i> No se adjuntaron archivos.
        </div>
      )}

      {archivos.length > 0 && (
        <div className="d-flex flex-wrap gap-3 mt-3 p-3 rounded border">
          {archivos.map((archivo) => (
            <div
              key={archivo.id}
              className="position-relative shadow-sm border d-flex flex-column align-items-center justify-content-center cursor-pointer"
              style={{ width: "100px", height: "100px", borderRadius: "8px" }}
              onClick={() => setPreviewFile(archivo)}
            >
              {archivo.tipo === "imagen" && (
                <img
                  src={archivo.url}
                  alt="Evidencia"
                  className="w-100 h-100"
                  style={{ objectFit: "cover", borderRadius: "8px" }}
                />
              )}

              {archivo.tipo === "pdf" && (
                <i className="fas fa-file-pdf text-danger fa-3x"></i>
              )}

              {(archivo.tipo === "video" && archivo.url.includes(".mov")) && (
                <div className="alert alert-secondary py-2 mt-2" role="alert" style={{ fontSize: '10px' }}>
                  <i className="fas fa-info-circle me-1"></i> Formato no soportado.
                </div>
              )}

              {(archivo.tipo === "video" && !archivo.url.includes(".mov")) && (
                <video
                  className="w-100 h-100"
                  style={{ objectFit: "cover", borderRadius: "8px" }}
                >
                  <source src={archivo.url}></source>
                </video>
              )}

              {!disabled && (
                <button
                  type="button"
                  className="btn btn-danger btn-sm position-absolute top-0 start-100 translate-middle rounded-circle shadow"
                  style={{ width: "22px", height: "22px", padding: 0, zIndex: 10 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeFile(archivo.id);
                  }}
                >
                  <i className="fas fa-times" style={{ fontSize: "10px" }}></i>
                </button>
              )}
              {archivo.tipo === "pdf" && (
                <small className="text-truncate w-100 px-1 text-center" style={{ fontSize: "10px", marginTop: "4px" }}>
                  {archivo.nombreOriginal || "Documento PDF"}
                </small>
              )}
            </div>
          ))}
        </div>
      )}

      {previewFile && createPortal(
        <>
          <div
            className="modal-backdrop fade show"
            style={{ zIndex: 1050, backgroundColor: "rgba(0,0,0,0.8)" }}
            onClick={() => setPreviewFile(null)}
          ></div>
          <div
            className="modal show d-block"
            style={{ zIndex: 1055, overflowY: "hidden" }}
            tabIndex={-1}
            onClick={() => setPreviewFile(null)}
          >
            <div
              className="modal-dialog modal-xl modal-dialog-centered"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-content overflow-hidden shadow-lg">
                <div className="modal-header bg-dark text-white border-0 py-2">
                  <h6 className="modal-title fw-bold text-truncate">
                    Previsualización
                    {previewFile.nombreSistema ? (
                      <small className="ms-2 text-light fw-normal">({previewFile.nombreSistema})</small>
                    ) : (
                      <small className="ms-2 text-light fw-normal">({previewFile.nombreOriginal})</small>
                    )}
                  </h6>
                  <button type="button" className="btn-close btn-close-white" onClick={() => setPreviewFile(null)}></button>
                </div>

                <div
                  className="modal-body p-0 position-relative bg-secondary bg-opacity-25"
                  style={{ height: "75vh" }}
                >
                  <a
                    href={previewFile.url}
                    download={previewFile.nombreOriginal || "descarga"}
                    className="btn btn-primary position-absolute shadow"
                    style={{ bottom: "20px", right: "20px", zIndex: 100 }}
                  >
                    <i className="fas fa-download me-2"></i> Descargar
                  </a>

                  {previewFile.tipo === "imagen" && (
                    <div
                      className="position-absolute start-50 translate-middle-x d-flex align-items-center gap-2 p-2 rounded shadow"
                      style={{ bottom: "20px", backgroundColor: "rgba(255,255,255,0.95)", zIndex: 100 }}
                    >
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setZoom(z => Math.max(100, z - 50))} disabled={zoom <= 100}>
                        <i className="fas fa-search-minus"></i>
                      </button>
                      <span className="fw-bold" style={{ minWidth: "50px", textAlign: "center" }}>{zoom}%</span>
                      <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => setZoom(z => Math.min(500, z + 50))} disabled={zoom >= 500}>
                        <i className="fas fa-search-plus"></i>
                      </button>
                      <button type="button" className="btn btn-sm btn-primary ms-1" onClick={() => setZoom(100)} title="Ajustar a pantalla">
                        <i className="fas fa-compress"></i>
                      </button>
                    </div>
                  )}

                  <div className="w-100 h-100 d-flex flex-column" style={{ overflow: "auto" }}>
                    <div style={{ margin: "auto", width: "100%", height: "100%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                      {previewFile.tipo === "imagen" && (
                        <img
                          src={previewFile.url}
                          alt="Preview"
                          style={
                            zoom === 100
                              ? { maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }
                              : { width: `${zoom}%`, height: "auto", transition: "width 0.2s ease-in-out" }
                          }
                        />
                      )}
                      {previewFile.tipo === "pdf" && (
                        <embed
                          src={previewFile.url}
                          type="application/pdf"
                          width="100%"
                          height="100%"
                        />
                      )}
                      {(previewFile.tipo === "video") && (
                        <video controls className="w-100 h-100" style={{ objectFit: "contain" }}>
                          <source src={previewFile.url}></source>
                        </video>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>,
        document.body
      )}
    </div>
  );
};